<!--
  The dashboard's map (logic/dashMap.ts): what is going on, at the top of
  the page, kept in sight under the top bar as the page scrolls.

  Each section is a card with its name and count, as wide as its share of
  the rows (never too narrow to name), its rows as cells grouped into runs
  by tone, most pressing first (your turn, failing, waiting, draft, idle,
  closed), so it reads as a few coloured runs. A cell is capped when the
  row is assigned to the viewer (full with a review asked, faint without).
  What the page shows is marked: its sections' cards outlined, its rows'
  cells underlined. Hovering a cell names its pull request; a press on a
  cell scrolls the page to that row (dragging keeps it following), on a
  card's name to its section.

  Each run's count, with its word ("12 your turn"; a run is at least as
  wide as both, and runs wrap in a narrow card), is
  the way to its rows for the keys and screen readers: it goes to the run's
  next row, round again, and takes the focus there, so the dashboard's keys
  go on from it. A card with a failing run has a filter in its head (`filters`), its funnel alone where its words would cut the card's name. The
  counts and filters are one toolbar, one tab stop, moved along by arrows,
  Home and End (`overviewKeys`); the cells and names are aria-hidden, the
  counts' labels saying the same.

  Rows are found on the page by what the dashboard marks them with:
  `data-map-tone` and `data-map-involve` on each row's focus stop, in
  sections (`section` with an `h2`); `data-map-title` names one whose text
  is not its title. A folded stack's head carries its members'. They are
  measured again on the next frame whenever the list's content changes.
-->
<script lang="ts">
  import { onDestroy, tick } from 'svelte';
  import { controlCommand } from '../keys/control';
  import { overviewKeys } from '../keys/controls';
  import { TONE_WORDS, nextTarget, overviewSections, runLabel, type MapInvolve, type MapTone, type OverviewCard, type OverviewRun } from '../logic/dashMap';

  interface Props {
    /** Whether the sections have rows to count yet: until then, nothing is said. */
    ready: boolean;
    /** The sections' list, whose rows the map finds and measures. */
    list: HTMLElement | undefined;
    /** Tones whose runs offer a filter beside their count, with what it does. */
    filters?: Partial<Record<MapTone, { title: string; apply: () => void }>>;
  }
  let { ready, list, filters = {} }: Props = $props();

  /** Laid out on the page now: not folded away. */
  const shown = (el: HTMLElement) => el.getClientRects().length > 0;

  /** The row each run's count last went to (by card and tone): the next is after it while it is still where it was put. */
  const last = new Map<string, HTMLElement>();
  /**
   * Goes to the next row of `run` (in card `card`) and focuses it: after
   * the one it last went to, while that is still in the middle of the
   * window (where it was put); else the first from the top of the window
   * down.
   */
  function go(card: number, run: OverviewRun) {
    const key = `${card}:${run.tone}`;
    const els = run.cells.map((c) => rows[c.index]).filter((el) => el && shown(el));
    const prev = last.get(key);
    const r = prev && els.includes(prev) ? prev.getBoundingClientRect() : null;
    const centred = !!r && Math.abs(r.top + r.height / 2 - window.innerHeight / 2) < 40;
    const i = nextTarget(
      els.map((el) => el.getBoundingClientRect().top + window.scrollY),
      centred ? r!.top + window.scrollY : window.scrollY - 2,
    );
    if (i < 0) return;
    last.set(key, els[i]);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    els[i].scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    els[i].focus({ preventScroll: true });
  }

  // --------------------------------------------------------------- the toolbar

  /** The toolbar's controls in order, and which takes the Tab key. */
  let controls: HTMLButtonElement[] = $state([]);
  let stop = $state(0);
  /** Arrows, Home and End move along the toolbar (see `overviewKeys`). */
  function key(e: KeyboardEvent) {
    const c = controlCommand(overviewKeys, e);
    const live = controls.filter((b) => b?.isConnected);
    const i = live.indexOf(e.currentTarget as HTMLButtonElement);
    if (!c || i < 0) return;
    const to = c === 'next' ? Math.min(i + 1, live.length - 1) : c === 'prev' ? Math.max(i - 1, 0) : c === 'first' ? 0 : live.length - 1;
    e.preventDefault();
    stop = to;
    live[to].focus();
  }
  /** A card's filter: the first of its runs' tones that has one (a failing run's), with that run's count. */
  const filterOf = (c: OverviewCard) => {
    const r = c.runs.find((r) => filters[r.tone]);
    return r ? { ...filters[r.tone]!, n: r.cells.length } : null;
  };
  /** Each control's place in the toolbar, in the order drawn: a card's filter (in its head), then its runs' counts. */
  const places = $derived.by(() => {
    let n = 0;
    return cards.map((c) => ({ filter: filterOf(c) ? n++ : -1, runs: c.runs.map(() => n++) }));
  });
  /** The control that takes the Tab key: the last one focused, while there is one there still. */
  const tabStop = $derived(Math.min(stop, places.reduce((n, p) => Math.max(n, p.filter, ...p.runs), 0)));

  // ------------------------------------------------------------- the overview

  let band: HTMLDivElement | undefined = $state();
  let cards = $state<OverviewCard[]>([]);
  /** Cards whose filter is its funnel alone: where its words would cut the card's name. */
  let compact = $state<boolean[]>([]);
  /** Which cards' sections, and which cells' rows, the page shows now. */
  let seenCards = $state<boolean[]>([]);
  let seenRows = $state<boolean[]>([]);
  /** The rows the cells stand for, in page order (a cell's `index`), and the sections the cards do. */
  let rows: HTMLElement[] = [];
  let sections: HTMLElement[] = [];

  function redraw() {
    if (!list) return;
    rows = [];
    sections = [];
    const read = [...list.querySelectorAll<HTMLElement>(':scope > section')].map((s) => {
      const els = [...s.querySelectorAll<HTMLElement>('[data-map-tone]')].filter(shown);
      if (els.length) sections.push(s);
      const at = rows.length;
      const cells = els.map((el, i) => ({
        index: at + i,
        tone: el.dataset.mapTone as MapTone,
        involve: (el.dataset.mapInvolve as MapInvolve | undefined) ?? null,
        title: el.dataset.mapTitle ?? el.textContent?.trim() ?? '',
      }));
      rows.push(...els);
      return { title: s.querySelector('h2')?.textContent?.trim() ?? '', cells, counting: s.getAttribute('aria-busy') === 'true' };
    });
    cards = overviewSections(read);
    void tick().then(fit);
    onscroll();
  }
  /**
   * Which cards' filters go to their funnel alone: those whose head is
   * narrower than the name at its full width, its count and the filter
   * with its words. Read the same whichever way the filter is drawn (its
   * words are kept, collapsed, so their width is still known), so it
   * settles at once.
   */
  function fit() {
    if (!band) return;
    compact = [...band.querySelectorAll<HTMLElement>('.card-head')].map((head) => {
      const filter = head.querySelector<HTMLElement>('.run-filter');
      if (!filter) return false;
      const name = head.querySelector<HTMLElement>('.card-name > span')!;
      const count = head.querySelector<HTMLElement>('.card-name > b')!;
      const words = filter.querySelector<HTMLElement>('.filter-word')!;
      const full = filter.offsetWidth + (head.classList.contains('compact') ? words.scrollWidth + 2 : 0);
      return name.scrollWidth + count.offsetWidth + NAME_GAP + HEAD_GAP + full > head.clientWidth;
    });
  }
  /** The gaps in a card's head (px): between its name and count, and before its filter (the styles below). */
  const NAME_GAP = 6;
  const HEAD_GAP = 8;

  /** What the page shows: between the overview's foot (it covers what is above) and the window's. */
  function onscroll() {
    const top = band?.getBoundingClientRect().bottom ?? 0;
    const seen = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return r.bottom > top && r.top < window.innerHeight;
    };
    seenRows = rows.map(seen);
    seenCards = sections.map(seen);
  }

  let frame = 0;
  /** Measures again on the next frame (one measure however many changes come before it). */
  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; redraw(); });
  }
  $effect(() => {
    if (!list) return;
    const content = new MutationObserver(schedule);
    content.observe(list, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden', 'data-map-tone', 'data-map-involve', 'aria-busy'] });
    const sized = new ResizeObserver(schedule);
    sized.observe(list);
    if (band) sized.observe(band);
    window.addEventListener('scroll', onscroll, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
    return () => {
      content.disconnect();
      sized.disconnect();
      window.removeEventListener('scroll', onscroll);
      window.removeEventListener('resize', schedule);
    };
  });
  onDestroy(() => cancelAnimationFrame(frame));

  /** Scrolls the page to what is under the pointer: a cell's row, in the middle of the window; a card's section, at its top. */
  function scrollTo(e: PointerEvent, behavior: ScrollBehavior) {
    const at = document.elementFromPoint?.(e.clientX, e.clientY) ?? (e.target as Element | null);
    const cell = at?.closest<HTMLElement>('[data-cell]');
    const card = at?.closest<HTMLElement>('[data-card]');
    if (cell) rows[Number(cell.dataset.cell)]?.scrollIntoView?.({ block: 'center', behavior });
    else if (card) sections[Number(card.dataset.card)]?.scrollIntoView?.({ block: 'start', behavior });
    onscroll();
  }
  function down(e: PointerEvent) {
    // A count or a filter is a button: it takes its own press.
    if (e.button !== 0 || (e.target as Element | null)?.closest?.('button')) return;
    // Not a focus target: the page keeps the focus, and its keys.
    e.preventDefault();
    band?.setPointerCapture?.(e.pointerId);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    scrollTo(e, reduce ? 'auto' : 'smooth');
  }
  function move(e: PointerEvent) {
    if (band?.hasPointerCapture?.(e.pointerId)) scrollTo(e, 'auto');
  }
</script>

{#if ready}
  <nav class="dash-map" aria-label="What's going on">
    {#if !cards.length}
      <p class="muted">Nothing on the dashboard yet.</p>
    {/if}
    <!-- svelte-ignore a11y_no_static_element_interactions (the cells are aria-hidden: the counts reach the same rows) -->
    <div class="overview" bind:this={band} role="toolbar" tabindex="-1" aria-label="Go to pull requests by state" aria-orientation="horizontal" onpointerdown={down} onpointermove={move}>
      {#each cards as c, i (i)}
        {@const f = filterOf(c)}
        {@const fat = places[i]?.filter ?? -1}
        <div class="card" class:seen={seenCards[i]} data-card={i} style:flex-grow={c.count}>
          <div class="card-head" class:compact={compact[i]}>
            <span class="card-name" title="{c.title}: {c.count}{c.counting ? ', still counting' : ''}" aria-hidden="true"><span>{c.title}</span><b class:counting-figure={c.counting}>{c.count}</b></span>
            {#if f}<button
                bind:this={controls[fat]}
                class="run-filter"
                tabindex={fat === tabStop ? 0 : -1}
                aria-label="{f.title} ({f.n} in {c.title})"
                title={f.title}
                onclick={f.apply}
                onkeydown={key}
                onfocus={() => (stop = fat)}
                ><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M1.5 2h9L7 6.2V10L5 9V6.2z" /></svg><span class="filter-word">only failing</span></button
              >{/if}
          </div>
          <div class="runs">
            {#each c.runs as r, j (r.tone)}
              {@const at = places[i]?.runs[j] ?? 0}
              <div class="run {r.tone}" style:flex-grow={r.cells.length} style:max-width="{Math.max(r.cells.length * 23, 64)}px">
                <span class="run-head">
                  <button
                    bind:this={controls[at]}
                    class="run-count"
                    data-tone={r.tone}
                    tabindex={at === tabStop ? 0 : -1}
                    aria-label={runLabel(r.cells.length, r.tone, c.title)}
                    title="{r.cells.length} {TONE_WORDS[r.tone].means}: go to the next"
                    onclick={() => go(i, r)}
                    onkeydown={key}
                    onfocus={() => (stop = at)}
                    ><b>{r.cells.length}</b><span class="run-word" aria-hidden="true">{TONE_WORDS[r.tone].word}</span></button
                  >
                </span>
                <div class="cells" class:counting-bar={c.counting} aria-hidden="true">
                  {#each r.cells as x (x.index)}
                    <i class="cell" class:both={x.involve === 'both'} class:assigned={x.involve === 'assigned'} class:seen={seenRows[x.index]} data-cell={x.index} title={x.title}></i>
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  </nav>
{/if}

<style>
  .dash-map { margin: 4px 0 14px; }
  .dash-map p { margin: 0 0 6px; font-size: 13px; }

  /* The tones, the same on a run's count and its cells: distinct hues, each strong in light and dark. */
  .you { --tone: var(--accent); }
  .failing { --tone: var(--bad); }
  .others { --tone: var(--syn-call); }
  .draft { --tone: var(--muted); }
  .none { --tone: var(--syn-keyword); }
  .closed { --tone: var(--line); }

  /* Kept in sight under the top bar: where the page is, at a glance. */
  .overview {
    position: sticky; z-index: 5; top: var(--topbar-h, 0px);
    display: flex; flex-wrap: wrap; gap: 8px; padding: 6px 0; background: var(--bg);
    cursor: pointer; touch-action: none; user-select: none;
  }
  /* A section: a card as wide as its runs' counts and words, then a share of the room left by its rows; never too narrow to name. Cards wrap to a new line before a card's runs do. */
  .card { flex: 1 1 max-content; max-width: 100%; min-width: 132px; padding: 6px 8px 8px; border: 1px solid var(--line); border-radius: 8px; background: var(--surface); box-shadow: 0 0 0 0 transparent; transition: border-color .12s, box-shadow .12s; }
  /* The sections the page shows. */
  .card.seen { border-color: var(--fg); box-shadow: inset 0 0 0 1px var(--fg); }
  .card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; font-size: 12px; line-height: 1.3; color: var(--heading, var(--fg)); white-space: nowrap; }
  .card-name { display: flex; align-items: baseline; gap: 6px; min-width: 0; flex: 1 1 auto; }
  .card-name span { font-weight: 600; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .card-name b { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--muted); }
  /* Its rows, as runs by tone: a few coloured blocks, each with its count. */
  /* Runs wrap in a card too narrow for every one's count and word. */
  .runs { display: flex; flex-wrap: wrap; gap: 4px; min-width: 0; }
  /* As wide as its rows at most (a cell is 22 px at most, a pixel apart), so a short run is not stretched thin. */
  /* At least as wide as its count and word, so every run is named without hovering. */
  .run { flex: 1 1 0; min-width: max-content; display: flex; flex-direction: column; gap: 2px; }
  /* A run's count and word: its way to its rows. The word goes where the run is too narrow for it (its title still says it). */
  .run-head { display: flex; min-width: 0; overflow: hidden; white-space: nowrap; }
  .run-count { display: inline-flex; align-items: baseline; gap: 3px; min-width: 0; padding: 0 2px; margin: 0 -2px; border: 0; border-radius: 3px; background: none; color: var(--tone); font: 600 11px/1.3 ui-sans-serif, system-ui, sans-serif; cursor: pointer; }
  .run-count b { font-weight: 700; font-variant-numeric: tabular-nums; }
  .run-count:hover .run-word, .run-count:focus-visible .run-word { text-decoration: underline; text-underline-offset: 2px; }
  .closed .run-count, .draft .run-count { color: var(--muted); }
  /* A card's filter (its failing run's): small, at the right of its head, there to find. */
  .run-filter { flex: none; margin-left: auto; display: inline-flex; align-items: center; gap: 2px; padding: 0 4px; border: 1px solid var(--line); border-radius: 4px; background: var(--surface); color: var(--muted); font: 500 10px/1.4 ui-sans-serif, system-ui, sans-serif; cursor: pointer; }
  .run-filter svg { width: 9px; height: 9px; fill: currentColor; }
  /* Where its words would cut the card's name, the filter is its funnel alone (its title and label still say it): the words kept, collapsed, so fit() can still read their width. */
  .filter-word { display: inline-block; overflow: hidden; white-space: nowrap; }
  .compact .filter-word { max-width: 0; }
  .compact .run-filter { gap: 0; padding: 2px 4px; }
  .run-filter:hover, .run-filter:focus-visible { color: var(--fg); border-color: var(--fg); }
  .cells { display: flex; gap: 1px; height: 20px; padding-top: 4px; }
  .cell { position: relative; flex: 1 1 0; min-width: 2px; max-width: 22px; height: 16px; border-radius: 2px; background: var(--tone); }
  .draft .cell { background: repeating-linear-gradient(135deg, var(--tone) 0 2px, transparent 2px 4px); box-shadow: inset 0 0 0 1px var(--tone); }
  .closed .cell { opacity: .7; }
  /* Failing checks: red, and broken across the middle, so it never reads as the your-turn orange beside it. */
  .failing .cell { background: linear-gradient(var(--tone) 0 6px, transparent 6px 9px, var(--tone) 9px); }
  /* Assigned to the viewer: a cap above the cell, full with a review asked (their turn), faint without. */
  .cell.both::before, .cell.assigned::before { content: ''; position: absolute; left: 0; right: 0; top: -4px; height: 3px; border-radius: 1px; background: var(--fg); }
  .cell.assigned::before { opacity: .45; }
  /* The rows the page shows: underlined, a bar that joins across neighbours. */
  .cells { padding-bottom: 5px; height: 25px; }
  .cell.seen::after { content: ''; position: absolute; left: -1px; right: 0; bottom: -5px; height: 3px; background: var(--fg); }

  @media (max-width: 600px) {
    .card { min-width: 120px; }
    /* Its cards stack a line each here: kept in sight, it would cover the page. */
    .overview { position: static; }
  }
</style>
